import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Radar, Mail, Lock, User as UserIcon, ArrowRight, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Ein Fehler ist aufgetreten. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [mode, setMode] = useState("login"); // "login" or "register"
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "client", contractor_type: "freelancer" });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/dashboard";
  const a = t.auth || {};
  const f = t.footer || {};
  const rolesT = t.roles || { client: "Auftraggeber", freelancer: "Auftragnehmer" };

  const handleChange = (e) => {
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register") {
      if (!form.name.trim()) { setError(a.err_name || "Bitte gib deinen Namen ein."); return; }
      if (!acceptTerms) { setError(a.terms_required || "Bitte akzeptiere die Nutzungsbedingungen, um fortzufahren."); return; }
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        const extras = { acceptTerms };
        if (form.role === "freelancer") extras.contractor_type = form.contractor_type;
        await register(form.email, form.password, form.name, form.role, extras);
      }
      navigate(redirectTo);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // For Google sign-in we cannot show the checkbox upfront (OAuth flow). The
    // TermsAcceptanceGate will require acceptance after the redirect, so the user
    // still has to confirm before they can use the platform.
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="min-h-screen pt-24 pb-16 flex items-center justify-center">
      <div className="w-full max-w-md px-4">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center neon-border rounded-xl">
            <Radar className="w-8 h-8 text-[#00F0FF]" />
          </div>
          <h1 className="font-['Outfit'] text-3xl font-light text-white">
            Project Radar <span className="neon-text">AR</span>
          </h1>
          <p className="text-[#A0A0B0] text-sm mt-2">
            {mode === "login" ? a.login_title : a.register_title}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex mb-8 glass-surface rounded-lg p-1">
          {["login", "register"].map(m => (
            <button
              key={m}
              data-testid={`auth-tab-${m}`}
              onClick={() => { setMode(m); setError(""); }}
              className={`flex-1 py-2.5 text-sm font-['Outfit'] font-medium rounded-md transition-all ${
                mode === m
                  ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20"
                  : "text-[#A0A0B0] hover:text-white"
              }`}
            >
              {m === "login" ? a.tab_login : a.tab_register}
            </button>
          ))}
        </div>

        {/* Form */}
        <AnimatePresence mode="wait">
          <motion.form
            key={mode}
            initial={{ opacity: 0, x: mode === "login" ? -20 : 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: mode === "login" ? 20 : -20 }}
            transition={{ duration: 0.2 }}
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            {mode === "register" && (
              <>
                <div>
                  <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">{a.name}</label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-3.5 w-4 h-4 text-[#606070]" />
                    <input
                      data-testid="register-name-input"
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Max Mustermann"
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] pl-10 pr-4 py-3 placeholder:text-white/30 text-sm transition-colors outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">{a.role}</label>
                  <div className="flex gap-3">
                    {[{ v: "client", l: rolesT.client }, { v: "freelancer", l: rolesT.freelancer }].map(r => (
                      <button
                        key={r.v}
                        type="button"
                        data-testid={`register-role-${r.v}`}
                        onClick={() => setForm(p => ({ ...p, role: r.v }))}
                        className={`flex-1 py-2.5 text-sm font-['Outfit'] rounded transition-all border ${
                          form.role === r.v
                            ? "border-[#00F0FF]/40 bg-[#00F0FF]/10 text-[#00F0FF]"
                            : "border-white/10 bg-white/5 text-[#A0A0B0] hover:bg-white/10"
                        }`}
                      >
                        {r.l}
                      </button>
                    ))}
                  </div>
                </div>
                {form.role === "freelancer" && (
                  <div data-testid="register-contractor-type-section">
                    <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">
                      {(t.contractor_types || {}).label || "Auftragnehmer-Typ"}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: "freelancer", l: (t.contractor_types || {}).freelancer || "Freelancer" },
                        { v: "unternehmen", l: (t.contractor_types || {}).unternehmen || "Unternehmen" },
                        { v: "mitarbeiter_auftraggeber", l: (t.contractor_types || {}).mitarbeiter_auftraggeber || "Mitarbeiter Auftraggeber" },
                        { v: "andere", l: (t.contractor_types || {}).andere || "Andere" },
                      ].map(ct => (
                        <button
                          key={ct.v}
                          type="button"
                          data-testid={`register-contractor-type-${ct.v}`}
                          onClick={() => setForm(p => ({ ...p, contractor_type: ct.v }))}
                          className={`px-3 py-2 text-xs font-['Outfit'] rounded transition-all border ${
                            form.contractor_type === ct.v
                              ? "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]"
                              : "border-white/10 bg-white/5 text-[#A0A0B0] hover:bg-white/10"
                          }`}
                        >
                          {ct.l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <div>
              <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">{a.email}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 w-4 h-4 text-[#606070]" />
                <input
                  data-testid="auth-email-input"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="dein@email.de"
                  required
                  className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] pl-10 pr-4 py-3 placeholder:text-white/30 text-sm transition-colors outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">{a.password}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3.5 w-4 h-4 text-[#606070]" />
                <input
                  data-testid="auth-password-input"
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] pl-10 pr-4 py-3 placeholder:text-white/30 text-sm transition-colors outline-none"
                />
              </div>
            </div>

            {/* Terms acceptance — required for registration */}
            {mode === "register" && (
              <label className="flex items-start gap-2.5 text-xs text-[#A0A0B0] cursor-pointer pt-1" data-testid="register-terms-row">
                <input
                  data-testid="register-terms-checkbox"
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => { setAcceptTerms(e.target.checked); setError(""); }}
                  className="mt-0.5 w-4 h-4 rounded border-white/20 accent-[#00F0FF] flex-shrink-0"
                />
                <span className="leading-relaxed">
                  {a.terms_pre}{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[#00F0FF] hover:underline"
                    data-testid="register-terms-link"
                  >
                    {a.terms_link || f.terms || "Nutzungsbedingungen"}
                  </Link>
                  {" "}{a.terms_post}
                </span>
              </label>
            )}

            {error && (
              <div data-testid="auth-error-msg" className="text-[#FF3B30] text-sm p-3 bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded">
                {error}
              </div>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading || (mode === "register" && !acceptTerms)}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {mode === "login" ? a.submit_login : a.submit_register}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-4 my-4">
              <div className="flex-1 border-t border-white/10" />
              <span className="text-xs text-[#606070]">{a.or || "oder"}</span>
              <div className="flex-1 border-t border-white/10" />
            </div>

            {/* Google Login */}
            <button
              data-testid="google-login-btn"
              type="button"
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-all font-['Outfit'] text-sm"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {a.google}
            </button>

            {mode === "login" && (
              <p className="text-center text-xs text-[#606070] mt-4">
                <Link to="/forgot-password" className="text-[#00F0FF] hover:underline">{a.forgot}</Link>
              </p>
            )}
          </motion.form>
        </AnimatePresence>
      </div>
    </div>
  );
}
